import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { Subscription } from 'rxjs';
import { MatTooltip } from '@angular/material/tooltip';
import { toPng } from 'html-to-image';
import { download } from 'src/app/utils/file';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { CouponVip } from 'src/app/shared/models/dtos/responses/coupon-vip/coupon-vip';
import { TopHeaderService } from 'src/app/core/services/top-header/top-header.service';
import { VipConfigSeriesDTO } from '../../../models/dtos/responses/vip-config-series-dto/vip-config-series-dto';

@Component({
  selector: 'app-product-coupon-vip-content',
  templateUrl: './product-coupon-vip-content.component.html',
  styleUrls: ['./product-coupon-vip-content.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Organisms that shows the main content of Product Coupon page
 */ // eslint-disable-next-line
export class ProductCouponVipContentComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() title!: string;
  @Input() label1!: string;
  @Input() label2!: string;
  @Input() label3!: string;
  @Input() coupon!: CouponVip;
  translatedStrings = {
    'copy-coupon-code': 'copy-coupon-code',
    'download-coupon': 'download-coupon',
    print: 'print',
    codeCopied: 'code-copied',
    downloadSuccess: 'successfully-downloaded',
    downloadFail: 'error-downloading-coupon',
    printFail: 'error-printing-coupon',
    printSuccess: 'successfully-printed',
  };

  @ViewChild('buttons') buttonsArea?: ElementRef<HTMLDivElement>;
  @ViewChild('tooltip') tooltip?: MatTooltip;
  @ViewChild('couponCard') couponCardComponent?: ElementRef<HTMLElement>;

  businessImgBase64: string = '';
  loading: boolean = false;
  private subsink = new Subscription();

  /**
   * @constructor
   */
  constructor(
    private clipboard: Clipboard,
    private translationService: TranslationService,
    private readonly topHeaderService: TopHeaderService,
    private readonly snackBarService: SnackBarService
  ) {}

  /**
   * on init
   */
  ngOnInit() {
    this.translationService.translateObject(this.translatedStrings);
    this.fetchBase64Img();
  }

  /**
   * business name
   */
  get businessName() {
    return this.topHeaderService.business?.businessName || '';
  }

  /**
   * business country
   */
  get businessCountry() {
    return this.topHeaderService.business?.country?.name || '';
  }

  /**
   * after view init
   */
  ngAfterViewInit() {
    let maxWidth = 0;
    const childNodes = this.buttonsArea?.nativeElement
      .childNodes as NodeListOf<HTMLDivElement>;
    if (childNodes) {
      childNodes.forEach((node: HTMLDivElement) => {
        const nodeWidth = node.clientWidth;
        if (nodeWidth > maxWidth) {
          maxWidth = nodeWidth;
        }
      });

      childNodes.forEach((node: HTMLDivElement) => {
        node.style.width = `${maxWidth}px`;
      });
    }
  }

  /**
   * cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * [TODO] Copy text to clipboard
   */
  copy() {
    this.clipboard.copy(this.coupon.code as string);
    this.snackBarService.success(this.translatedStrings.codeCopied);
  }

  /**
   * Save a coupon as png
   */
  downloadCoupon() {
    if (this.couponCardComponent?.nativeElement) {
      toPng(this.couponCardComponent?.nativeElement)
        .then((couponCardPng) => {
          download(`coupon-${this.coupon.code}.png`, couponCardPng);
          this.snackBarService.success(this.translatedStrings.downloadSuccess);
        })
        .catch(() => {
          this.snackBarService.error(this.translatedStrings.downloadFail);
        });
    }
  }

  /**
   * Saves a coupon as pdf
   */
  printCoupon() {
    if (this.couponCardComponent?.nativeElement) {
      toPng(this.couponCardComponent?.nativeElement)
        .then((couponCardPng) => {
          const w = window.open('', '_new');
          if (w) {
            const img = w.document.createElement('img');
            img.src = couponCardPng;
            img.onload = () => {
              w.document.body.appendChild(img);
              w.onafterprint = () => {
                w.close();
              };
              w.print();
            };
          }
          this.snackBarService.success(this.translatedStrings.printSuccess);
        })
        .catch(() => {
          this.snackBarService.error(this.translatedStrings.printFail);
        });
    }
  }

  /**
   * asynchronous image fetching
   */
  private async fetchBase64Img() {
    if (this.coupon.business.imgSource) {
      this.businessImgBase64 = await this.convertUrlToBase64(
        this.coupon.business.imgSource
      );
    }
  }

  /**
   *
   * @param {string} url
   * @return {any}
   */
  private async convertUrlToBase64(url: string): Promise<string> {
    const options: RequestInit = {
      responseType: 'blob',
    } as RequestInit;

    const response = await fetch(url, options);
    const blob = await response.blob();
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.addEventListener('load', () => {
        resolve(reader.result as string);
      });
      reader.addEventListener('error', (event) => {
        reject(event);
      });
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Get Promotional Text by active language
   * @param {VipConfigSeriesDTO} vipConfig
   * @return {string}
   */
  getPromoTextByLanguage(vipConfig?: VipConfigSeriesDTO): string {
    if (!vipConfig) return '';

    const language = this.translationService.getActiveLang();

    const textByLanguage: Record<'en' | 'es' | 'pt-BR', string> = {
      en: vipConfig.promotionalTextEnglish,
      es: vipConfig.promotionalTextSpanish,
      'pt-BR': vipConfig.promotionalTextPortuguese,
    };

    return textByLanguage[language];
  }
}
